import { Component, computed, inject, Injector, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	AddressFieldData, AngularAddressField, AngularAddressFieldWidget, AngularCommonForm, AngularDateField, AngularNumberField, AngularNumberFieldWidget, AngularSelectField, AngularSelectFieldWidget, AngularTextField, AngularTextFieldWidget
} from '@eforall/common';
import { DialogAction } from '../../dialog-action';
import { DialogContext } from '../../dialog-data';
import { DashboardDialogFramePart } from '../../dialog-frame/dialog-frame.part';


// Fields this dialog can render dynamically. Keep this union in sync with the template switch cases.
type DialogAngularField = | AngularTextField | AngularNumberField | AngularDateField | AngularSelectField<unknown> | AngularAddressField;

// Helper to strip internal properties and bind formFieldName to key K
type BindField<F, K extends string> = F extends unknown ? Omit<F, 'formField' | 'formFieldName'> & { formFieldName: K } : never;

// Builds a type-safe union of allowed dialog field configs from VALUES.
// Each key K only accepts field types compatible with VALUES[K], and callers
// pass input-only config (no `formField`) with `formFieldName` locked to K.
type Field<VALUES> = {
	[K in keyof VALUES & string]: BindField<
		| (NonNullable<VALUES[K]> extends string ? AngularTextField<VALUES[K]> : never)
		| (NonNullable<VALUES[K]> extends number ? AngularNumberField<VALUES[K]> : never)
		| (NonNullable<VALUES[K]> extends Date ? AngularDateField<VALUES[K]> : never)
		| (NonNullable<VALUES[K]> extends AddressFieldData ? AngularAddressField<VALUES[K]> : never)
		| AngularSelectField<VALUES[K]>,
		K
	>;
}[keyof VALUES & string]

export interface AngularFormDialogData<VALUES = {}> {
	header: string,
	fields: Field<VALUES>[],
	saveBtn?: string, // Optional custom text for the save button. Defaults to 'Save'.
	onSave: (values: VALUES) => Promise<void>,
}

@Component({
	selector: 'angular-form-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		AngularTextFieldWidget,
		AngularNumberFieldWidget,
		AngularSelectFieldWidget,
		AngularAddressFieldWidget,
	],
	templateUrl: './angular-form.dialog.html',
})

export class AngularFormDialog<VALUES = {}> {

	public dialogRef = inject(MatDialogRef<AngularFormDialog<VALUES>>);
	public dialogContext: DialogContext<AngularFormDialogData<VALUES>> = inject(MAT_DIALOG_DATA);


	readonly commonForm = new AngularCommonForm<VALUES>(inject(Injector));

	readonly fields: DialogAngularField[] = [];

	constructor() {
		const dialogFields = this.dialogContext.data?.fields ?? [];

		if (dialogFields.length === 0) throw new Error('No fields provided to AngularFormDialog');

		for (const field of dialogFields) {
			const addedField = this.commonForm.addField(field);
			this.fields.push(addedField as DialogAngularField);
		}

		this.commonForm.build();
	}

	actions: DialogAction[] = [
		{
			id: 'save',
			enabled: signal(true),
			cssClass: computed(() => this.commonForm.isValid() ? 'btn-primary' : undefined),
			visible: signal(true),
			text: computed(() => this.dialogContext.data?.saveBtn ?? 'Save'),
			linkType: 'callback',
			callback: async () => {

				if (!this.dialogContext.data) return;

				if (!this.commonForm.touchAndValidate()) return;

				const value = this.commonForm.value();

				try {
					await this.dialogContext.data.onSave(value);
				} catch (err) {
					console.error('Failed to save form', err);
				}
				await this.dialogRef.close();
			},
			willCloseDialog: false,
		},
		{
			id: 'revert',
			enabled: signal(true),
			cssClass: signal(undefined),
			visible: signal(true),
			text: signal('Revert'),
			linkType: 'callback',
			callback: async () => {

				if (!this.dialogContext.data) return;

				this.commonForm.reset();
			},
			willCloseDialog: false,
		},
		{
			id: 'close',
			enabled: signal(true),
			cssClass: signal(undefined),
			visible: signal(true),
			text: signal('Close'),
			linkType: 'no-action',
			willCloseDialog: true,
		}
	];

}